/**
 * My files — the browser.
 *
 * The prototype draws folder cards and a file table but has no folder-detail
 * view, no breadcrumbs and no context menus, so navigation is originated here.
 * It keeps the prototype's table skeleton (38px type tile, name, shared, size,
 * modified) and its 16px card grid, and adds the breadcrumb trail and row
 * actions the real thing needs.
 *
 * Two behaviours follow from encryption rather than from design:
 *
 *   · sorting and searching happen after decryption, in memory, because the
 *     server cannot read a name
 *   · the grid view shows a thumbnail only when one was generated and
 *     encrypted at upload time; there is no server-side image processing and
 *     there cannot be
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, errorText, type NodeWire } from '../api';
import { ds } from '../ds';
import { Link, useRouter } from '../router';
import { useApp } from '../state';
import { entitlements } from '@shared/entitlements';
import {
  DataRow,
  DataTable,
  Eyebrow,
  PageTitle,
  Panel,
  type Column
} from '../components/Shell';
import { useIsNarrow } from '../responsive';
import { ShareDialog } from '../components/ShareDialog';
import { SendToReviewDialog } from '../components/SendToReviewDialog';
import { Preview } from '../components/Preview';
import {
  compareNodes,
  createFolder,
  downloadToDisk,
  ensureFolderPath,
  moveNode,
  getCachedKey,
  keyForNode,
  listFolder,
  renameNode,
  searchTree,
  thumbnailUrl,
  uploadFile,
  type DecryptedNode
} from '../nodes';
import { canThumbnail, makeThumbnail } from '../thumbnails';
import { dragHasFiles, fromDirectoryPicker, readDrop } from '../dnd';
import { registerNewActions, takePendingNewAction } from '../newactions';
import { UploadQueue, summarise, type QueueItem } from '../uploadqueue';
import { EASE, fileIcon, formatBytes, formatWhen, monoPlain, tint } from '../theme';
import { SkeletonRows } from '../components/Skeleton';
import { useT } from '../i18n';

type View = 'list' | 'grid';

export function Files({
  folderId,
  root
}: {
  folderId?: string;
  /**
   * Where this browser's tree starts, when it is not the personal one.
   *
   * A workspace passes its own root here. Everything downstream — the
   * breadcrumb's home link, the "top of the tree" title, and the scope search
   * crawls — keys off it, so the same component serves both without a second
   * copy of the file browser.
   */
  root?: { id: string; label: string; href: string };
}) {
  const t = useT();
  const { Button, Input } = ds();
  const { me, refreshStorage } = useApp();
  const { navigate } = useRouter();

  const rootId = root?.id ?? me?.rootNodeId ?? null;
  const rootLabel = root?.label ?? 'My files';
  const rootHref = root?.href ?? '/';
  const currentId = folderId ?? rootId;

  const [contents, setContents] = useState<Awaited<ReturnType<typeof listFolder>> | null>(null);
  const [raw, setRaw] = useState<{ parent: NodeWire; ancestors: NodeWire[]; nodes: NodeWire[] } | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [view, setView] = useState<View>('list');
  const [query, setQuery] = useState('');
  const narrow = useIsNarrow();
  const [results, setResults] = useState<DecryptedNode[] | null>(null);
  const [uploads, setUploads] = useState<QueueItem[]>([]);
  const [dragging, setDragging] = useState(false);
  const queueRef = useRef<UploadQueue | null>(null);
  // dragenter/dragleave fire for every child the pointer crosses, so a boolean
  // flickers the overlay as it moves over rows. Counting enters and leaves is
  // the standard fix and the only one that survives nested elements.
  const dragDepth = useRef(0);
  const [sharing, setSharing] = useState<DecryptedNode | null>(null);
  const [sendingToReview, setSendingToReview] = useState<DecryptedNode | null>(null);
  // Client-side gate, matching how the nav and Courses.tsx do it: the action is
  // hidden rather than offered and refused. Both Workers check it again — Cloud
  // before it opens the handoff, and Review before it creates the project.
  const canReview = entitlements(me?.plan).reviewProjects !== 0;
  const [previewing, setPreviewing] = useState<DecryptedNode | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    if (!currentId) return;
    setLoading(true);
    setError(null);
    try {
      const wire = await api.nodes.list(currentId);
      setRaw(wire);
      setContents(await listFolder(currentId));
    } catch (err) {
      setError(errorText(err, 'Could not open that folder.'));
    } finally {
      setLoading(false);
    }
  }, [currentId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Search walks the tree client-side, so it is debounced harder than a
  // server-side one would be — each keystroke could otherwise start a crawl.
  useEffect(() => {
    if (!query.trim() || !rootId) {
      setResults(null);
      return;
    }
    const timer = setTimeout(() => {
      void searchTree(rootId, query).then(setResults).catch(() => setResults([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [query, rootId]);

  const upload = useCallback(
    async (dropped: { file: File; path: string[] }[]) => {
      if (!raw) return;
      setUploadError(null);

      // The caller's own limit, not the API's sanity bound. `null` is no limit,
      // which is the ordinary state for an organisation that has not set one.
      const limit = me?.maxFileBytes ?? null;
      const tooBig = limit === null ? [] : dropped.filter((d) => d.file.size > limit);
      const queued = limit === null ? dropped : dropped.filter((d) => d.file.size <= limit);
      if (tooBig.length) {
        // The cap is per caller — an organisation sets its own — so it comes
        // from /me rather than the unauthenticated config
        // rather than the "5 GB" that used to be hardcoded into this sentence
        // while Demo's real limit was 0.25 GB.
        setUploadError(
          tooBig.length === 1
            ? `${tooBig[0]!.file.name} is larger than the ${formatBytes(limit ?? 0)} limit.`
            : `${tooBig.length} files are larger than the ${formatBytes(limit ?? 0)} limit.`
        );
      }
      if (!queued.length) return;

      const queue = new UploadQueue();
      queueRef.current = queue;
      const stop = queue.onChange(() => setUploads(queue.items()));
      queue.add(
        queued.map((d) => ({
          name: d.file.name,
          size: d.file.size,
          path: d.path,
          payload: d
        }))
      );

      // Scoped to this drop: a folder of 300 files in 12 directories would
      // otherwise ask the server to create the same 12 folders 300 times.
      const folders = new Map<string, { parent: NodeWire; chain: NodeWire[] }>();

      await queue.start(async (entry, ctx) => {
        const { file, path } = entry.payload as { file: File; path: string[] };
        const { parent, chain } = path.length
          ? await ensureFolderPath(raw.parent, raw.ancestors, path, folders)
          : { parent: raw.parent, chain: raw.ancestors };

        // Uploading over a name that already exists in this folder replaces
        // that file — a new revision of the same node — rather than failing on
        // the sibling-name constraint. Only checked at the drop root: a folder
        // this drop just created has nothing in it to replace.
        const existing = path.length
          ? undefined
          : contents?.nodes.find(
              (n) => n.kind === 'file' && n.name.toLowerCase() === file.name.toLowerCase()
            );

        const node = await uploadFile(file, parent, chain, {
          existingNodeId: existing?.id,
          onProgress: (p) =>
            ctx.onProgress({ bytesDone: p.bytesDone, chunk: p.chunk, chunks: p.chunks })
        });

        // After the upload rather than before, so a file that cannot be drawn —
        // or a browser that cannot decode it — never blocks the upload.
        if (canThumbnail(file.type)) {
          const nodeKey = getCachedKey(node.id);
          if (nodeKey) {
            const thumb = await makeThumbnail(file, nodeKey);
            if (thumb) await api.nodes.putThumbnail(node.id, thumb.slice().buffer as ArrayBuffer);
          }
        }
      });

      stop();
      queueRef.current = null;

      // Held apart from `error`, which `load()` clears — and this function ends
      // by calling `load()`, so a failure written there was wiped a few hundred
      // milliseconds later and the upload appeared to succeed.
      const failed = queue.items().filter((i) => i.status === 'failed');
      if (failed.length) {
        setUploadError(
          failed.length === 1
            ? `${failed[0]!.name}: ${failed[0]!.error ?? 'upload failed'}`
            : `${failed.length} files could not be uploaded.`
        );
      }
      setUploads([]);
      await load();
      await refreshStorage();
    },
    [raw, contents, load, refreshStorage, me?.maxFileBytes]
  );

  /** The file picker still hands over a flat FileList. */
  const uploadFiles = useCallback(
    (files: FileList | File[]) => upload(Array.from(files).map((file) => ({ file, path: [] }))),
    [upload]
  );

  /** The folder picker hands over the same flat list with paths attached. */
  const uploadFolder = useCallback(
    (files: FileList | File[]) => upload(fromDirectoryPicker(files)),
    [upload]
  );

  // Dropping just outside the container navigated the browser to the file —
  // the page vanished and the vault locked. The window-level guard is what
  // stops that, and it has to be on the window rather than the container
  // precisely because the misses happen outside it.
  useEffect(() => {
    const swallow = (e: DragEvent) => {
      if (dragHasFiles(e.dataTransfer)) e.preventDefault();
    };
    window.addEventListener('dragover', swallow);
    window.addEventListener('drop', swallow);
    return () => {
      window.removeEventListener('dragover', swallow);
      window.removeEventListener('drop', swallow);
    };
  }, []);

  /**
   * Drag a row onto a folder row.
   *
   * The re-key is `moveNode`'s job: a node's key is wrapped under its parent's,
   * so a move is a rewrap plus a fresh name HMAC, and the file's bytes are never
   * touched. What is done here is the part that needs the page's state — finding
   * both wire nodes and their ancestor chains.
   */
  const moveInto = useCallback(
    async (sourceId: string, targetFolderId: string) => {
      if (!raw) return;
      const source = raw.nodes.find((n) => n.id === sourceId);
      const target = raw.nodes.find((n) => n.id === targetFolderId);
      const name = contents?.nodes.find((n) => n.id === sourceId)?.name;
      if (!source || !target || !name) return;

      const chain = [raw.parent, ...raw.ancestors];
      try {
        // The target's chain is the current folder's chain with the current
        // folder on the front — the target is a child of it.
        await moveNode(source, chain, name, target, chain);
        await load();
      } catch (err) {
        setError(errorText(err, 'Could not move that.'));
      }
    },
    [raw, contents, load]
  );

  const newFolder = useCallback(async () => {
    if (!raw) return;
    const name = window.prompt('Name this folder');
    if (!name) return;
    try {
      await createFolder(raw.parent, raw.ancestors, name);
      await load();
    } catch (err) {
      setError(errorText(err, 'Could not create that folder.'));
    }
  }, [raw, load]);

  /**
   * Lend the sidebar's New menu the three things only this page can do.
   *
   * Re-registered whenever the current folder changes, because each of them
   * closes over it. Unregistered on the way out so the menu falls back to
   * navigating here rather than creating a folder in a folder that is no
   * longer on screen.
   */
  useEffect(() => {
    if (!raw) return;
    registerNewActions({
      newFolder: () => void newFolder(),
      uploadFiles: () => fileInput.current?.click(),
      uploadFolder: () => folderInput.current?.click()
    });
    return () => registerNewActions(null);
  }, [raw, newFolder]);

  /**
   * Something was asked for from a route that had no folder.
   *
   * Opening a file picker needs transient user activation, and the click that
   * asked for this happened before a pushState navigation — Chromium's window
   * comfortably covers that, but a browser is within its rights to refuse. The
   * failure is benign: you are standing on the file list with the same two
   * buttons in the toolbar.
   */
  useEffect(() => {
    if (!raw) return;
    const pending = takePendingNewAction();
    if (pending === 'newFolder') void newFolder();
    else if (pending === 'uploadFiles') fileInput.current?.click();
    else if (pending === 'uploadFolder') folderInput.current?.click();
    // Only when the folder first becomes available, not on every change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!raw]);

  const shown = results ?? contents?.nodes ?? [];

  if (!rootId) return null;

  return (
    <div
      onDragEnter={(e) => {
        if (!dragHasFiles(e.dataTransfer)) return;
        dragDepth.current += 1;
        setDragging(true);
      }}
      onDragOver={(e) => {
        // A drag carrying no files — a link, a selection, an image from another
        // page — is left alone. Accepting one shows the overlay and then does
        // nothing, which reads as broken.
        if (!dragHasFiles(e.dataTransfer)) return;
        e.preventDefault();
        // Without this the OS cursor shows "move", and a file manager that
        // believes a move happened is a file manager about to delete something.
        e.dataTransfer.dropEffect = 'copy';
      }}
      onDragLeave={() => {
        dragDepth.current -= 1;
        if (dragDepth.current <= 0) {
          dragDepth.current = 0;
          setDragging(false);
        }
      }}
      onDrop={(e) => {
        e.preventDefault();
        dragDepth.current = 0;
        setDragging(false);
        if (!dragHasFiles(e.dataTransfer)) return;
        // Read synchronously, inside the handler: a DataTransfer is dead the
        // moment this returns, and a read after that yields nothing — which
        // looks exactly like an empty folder rather than a bug.
        void readDrop(e.dataTransfer).then((dropped) => {
          if (dropped.length) void upload(dropped);
        });
      }}
      style={{ display: 'grid', gap: 24, position: 'relative' }}
    >
      {dragging && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(121,108,191,.08)',
            border: '2px dashed var(--primary)',
            borderRadius: 22,
            display: 'grid',
            placeItems: 'center',
            zIndex: 10,
            pointerEvents: 'none'
          }}
        >
          <div style={{ ...monoPlain, fontSize: 14, color: 'var(--primary-text)' }}>
            {t('files.drop-to-upload')}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <Breadcrumbs
            ancestors={contents?.ancestors ?? []}
            current={contents?.parent}
            rootId={rootId}
            rootLabel={rootLabel}
            rootHref={rootHref}
          />
          <PageTitle
            title={
              currentId === rootId ? `${rootLabel}.` : `${contents?.parent.name ?? 'Loading'}.`
            }
            meta={
              contents
                ? `${contents.nodes.length} item${contents.nodes.length === 1 ? '' : 's'}`
                : undefined
            }
          />
        </div>

        {/* On a phone the search field takes the full width and the controls
            sit under it, rather than four items competing for 360px. */}
        <div
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'center',
            flexWrap: 'wrap',
            width: narrow ? '100%' : undefined,
            // Takes the row's spare room and pushes its controls right, which
            // looks as it did on a wide screen and gives the search field
            // somewhere to shrink into on a narrow one.
            ...(narrow ? {} : { flex: '1 1 auto', justifyContent: 'flex-end' })
          }}
        >
          {/* A hard 260px, once, which never gave way: the controls wrapped
              round it instead. It shrinks to 160 first now. */}
          <div style={narrow ? { width: '100%' } : { flex: '1 1 200px', minWidth: 160, maxWidth: 260 }}>
            <Input
              placeholder={t('files.search-your-files')}
              value={query}
              onChange={(e) => setQuery((e.target as HTMLInputElement).value)}
            />
          </div>
          <Button variant="secondary" size="md" onClick={newFolder}>
            {t('files.new-folder')}
          </Button>
          <Button variant="secondary" size="md" onClick={() => fileInput.current?.click()}>
            {t('files.upload')}
          </Button>
          <Segmented view={view} onChange={setView} />
        </div>
      </div>

      <input
        ref={fileInput}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          if (e.target.files?.length) void uploadFiles(e.target.files);
          e.target.value = '';
        }}
      />

      {/* `webkitdirectory` is not in React's typings and is not going to be —
          it is a non-standard attribute every browser implements. */}
      <input
        ref={folderInput}
        type="file"
        multiple
        hidden
        {...({ webkitdirectory: '' } as Record<string, string>)}
        onChange={(e) => {
          if (e.target.files?.length) void uploadFolder(e.target.files);
          e.target.value = '';
        }}
      />

      {(error || uploadError) && (
        <Panel style={{ padding: '14px 20px', background: 'var(--danger-100)' }}>
          <span style={{ fontSize: 13.5, color: 'var(--danger-600)' }}>{error ?? uploadError}</span>
        </Panel>
      )}

      {uploads.length > 0 && <UploadPanel items={uploads} queue={queueRef.current} />}

      {results && (
        <div style={monoPlain}>
          {results.length} result{results.length === 1 ? '' : 's'} for “{query}”
        </div>
      )}

      {loading && !contents ? (
        <Panel style={{ paddingTop: 6, paddingBottom: 6 }}>
          <SkeletonRows rows={7} label={t('files.decrypting-your-files')} />
        </Panel>
      ) : shown.length === 0 ? (
        <EmptyState onChoose={() => fileInput.current?.click()} searching={!!results} />
      ) : view === 'list' ? (
        <FileTable
          onMove={moveInto}
          nodes={shown}
          onOpen={(n) => (n.kind === 'folder' ? navigate(`/files/${n.id}`) : setPreviewing(n))}
          onShare={setSharing}
          onDownload={async (n) => {
            const key = getCachedKey(n.id);
            if (key) await downloadToDisk(n, key);
          }}
          onRename={async (n) => {
            if (!raw || !contents) return;
            const name = window.prompt('Rename', n.name);
            if (!name) return;
            const wire = raw.nodes.find((w) => w.id === n.id);
            if (!wire) return;
            const parentKey = await keyForNode(raw.parent, raw.ancestors);
            await renameNode(wire, [raw.parent, ...raw.ancestors], parentKey, name);
            await load();
          }}
          onTrash={async (n) => {
            await api.nodes.trash(n.id);
            await load();
          }}
          onSendToReview={canReview ? (n) => setSendingToReview(n) : null}
        />
      ) : (
        <FileGrid
          nodes={shown}
          onOpen={(n) => (n.kind === 'folder' ? navigate(`/files/${n.id}`) : setPreviewing(n))}
        />
      )}

      {sendingToReview && (
        <SendToReviewDialog
          node={sendingToReview}
          nodeKey={getCachedKey(sendingToReview.id)}
          onClose={() => setSendingToReview(null)}
        />
      )}

      {sharing && (
        <ShareDialog
          node={sharing}
          onClose={() => setSharing(null)}
          nodeKey={getCachedKey(sharing.id)}
        />
      )}

      {previewing && (
        <Preview
          node={previewing}
          nodeKey={getCachedKey(previewing.id)}
          onClose={() => setPreviewing(null)}
          onRestored={() => {
            setPreviewing(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

// ── pieces ──────────────────────────────────────────────────────────────────

/**
 * The upload panel.
 *
 * A dropped folder can be hundreds of files, so this shows a total and the few
 * that are actually moving rather than a row per file — a list that scrolls past
 * the fold tells you less than one line saying "18 of 240".
 *
 * Cancel is per row and for the whole queue, because both are things people
 * want: one wrong 4 GB file, or the wrong folder entirely.
 */
function UploadPanel({ items, queue }: { items: QueueItem[]; queue: UploadQueue | null }) {
  const t = useT();
  const { Button, Progress } = ds();
  const totals = summarise(items);
  const active = items.filter((i) => i.status === 'running');
  const percent = totals.bytesTotal
    ? Math.round((totals.bytesDone / totals.bytesTotal) * 100)
    : 0;

  return (
    <Panel style={{ padding: '16px 22px', display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <Eyebrow>{t('files.uploading')}</Eyebrow>
        <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={monoPlain}>
            {totals.done} / {totals.total}
            {totals.failed > 0 ? ` · ${totals.failed} failed` : ''}
          </span>
          <span style={{ cursor: 'pointer', display: 'inline-flex' }} onClick={() => queue?.cancelAll()}>
            <Button variant="ghost" size="sm">
              {t('files.cancel-all')}
            </Button>
          </span>
        </span>
      </div>

      <Progress value={percent} tone="signal" size="sm" />

      {active.map((u) => (
        <div key={u.id} style={{ display: 'grid', gap: 6 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <span style={{ fontSize: 13.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {u.path.length ? `${u.path.join('/')}/` : ''}
              {u.name}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 'none' }}>
              <span style={monoPlain}>
                {u.chunks ? `${u.chunk} / ${u.chunks} · ` : ''}
                {formatBytes(u.bytesDone)}
              </span>
              <span style={{ cursor: 'pointer', display: 'inline-flex' }} onClick={() => queue?.cancel(u.id)}>
                <Button variant="ghost" size="sm">
                  Cancel
                </Button>
              </span>
            </span>
          </div>
          <Progress
            value={u.chunks ? Math.round((u.chunk / u.chunks) * 100) : 0}
            tone="signal"
            size="sm"
          />
        </div>
      ))}
    </Panel>
  );
}

function Breadcrumbs({
  ancestors,
  current,
  rootId,
  rootLabel,
  rootHref
}: {
  ancestors: DecryptedNode[];
  current?: DecryptedNode;
  rootId: string;
  /** What the top of this tree is called and where it lives — "My files" at
   *  `/`, or the workspace's name at `/workspaces/:id`. */
  rootLabel: string;
  rootHref: string;
}) {
  const { Icon } = ds();
  // `ancestors` arrives nearest-first; a trail reads the other way.
  const trail = [...ancestors].reverse();
  if (!current || current.id === rootId) return null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
      {trail.map((node) => (
        <React.Fragment key={node.id}>
          <Link to={node.id === rootId ? rootHref : `/files/${node.id}`}>
            <span style={{ ...monoPlain, color: 'var(--text-link)' }}>
              {node.id === rootId ? rootLabel : node.name}
            </span>
          </Link>
          <Icon name="chevron-right" size={13} />
        </React.Fragment>
      ))}
      <span style={monoPlain}>{current.name}</span>
    </div>
  );
}

function Segmented({ view, onChange }: { view: View; onChange: (v: View) => void }) {
  const { Icon } = ds();
  return (
    <div
      style={{
        display: 'inline-flex',
        padding: 3,
        borderRadius: 999,
        background: 'var(--surface-sunken)',
        gap: 2
      }}
    >
      {(['list', 'grid'] as const).map((mode) => (
        <button
          key={mode}
          aria-label={mode === 'list' ? 'List view' : 'Grid view'}
          onClick={() => onChange(mode)}
          style={{
            width: 32,
            height: 30,
            borderRadius: 999,
            border: 'none',
            cursor: 'pointer',
            background: view === mode ? 'var(--surface-card)' : 'transparent',
            color: view === mode ? 'var(--primary-text)' : 'var(--text-muted)'
          }}
        >
          <Icon name={mode === 'list' ? 'list' : 'layout-grid'} size={15} />
        </button>
      ))}
    </div>
  );
}

/**
 * The table, with rows that can be dragged into folders.
 *
 * `moveNode` has been correct and uncalled since it was written — it re-wraps
 * the node key under the new parent and recomputes the name HMAC, which is
 * exactly what `PATCH /api/cloud/nodes/:id` demands. This is the gesture that
 * finally calls it.
 *
 * Only folders are drop targets, and a folder is never a target for itself.
 * Dropping a folder into its own descendant would orphan a subtree, and the
 * server's parent check would reject it — but by then the person has already
 * seen a drop indicator that promised it would work.
 */
function FileTable({
  nodes,
  onOpen,
  onShare,
  onDownload,
  onRename,
  onTrash,
  onSendToReview,
  onMove
}: {
  nodes: DecryptedNode[];
  onOpen: (n: DecryptedNode) => void;
  onShare: (n: DecryptedNode) => void;
  onDownload: (n: DecryptedNode) => void;
  onRename: (n: DecryptedNode) => void;
  onTrash: (n: DecryptedNode) => void;
  /** Null when this account's plan has no Review, which hides the action. */
  onSendToReview: ((n: DecryptedNode) => void) | null;
  onMove?: (sourceId: string, targetFolderId: string) => void;
}) {
  const t = useT();
  const sorted = useMemo(() => [...nodes].sort(compareNodes), [nodes]);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  return (
    <DataTable columns={FILE_COLUMNS}>
      {sorted.map((node, i) => {
        const isTarget =
          !!onMove && node.kind === 'folder' && !!draggingId && draggingId !== node.id;
        return (
        <DataRow
          key={node.id}
          columns={FILE_COLUMNS}
          first={i === 0}
          // Files open the viewer now, so every row is openable. The viewer
          // handles a type it cannot draw by offering the download.
          onOpen={() => onOpen(node)}
          drag={
            onMove
              ? {
                  id: node.id,
                  over: overId === node.id,
                  onDragStart: () => setDraggingId(node.id),
                  onDragEnd: () => {
                    setDraggingId(null);
                    setOverId(null);
                  },
                  onDragOver: (e) => {
                    if (!isTarget) return;
                    // Only with preventDefault does the row become a drop
                    // target at all — the default is to refuse.
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    setOverId(node.id);
                  },
                  onDragLeave: () => setOverId((cur) => (cur === node.id ? null : cur)),
                  onDrop: (e) => {
                    if (!isTarget) return;
                    e.preventDefault();
                    e.stopPropagation();  // not an upload into the current folder
                    const sourceId = e.dataTransfer.getData('text/plain');
                    setOverId(null);
                    setDraggingId(null);
                    if (sourceId && sourceId !== node.id) onMove(sourceId, node.id);
                  }
                }
              : undefined
          }
          tile={<TypeTile node={node} />}
          cells={[
            <NodeName node={node} />,
            node.kind === 'folder' ? '—' : formatBytes(node.plainSizeBytes),
            formatWhen(node.updatedAt)
          ]}
          actions={
            <>
              {node.kind === 'file' && (
                <RowAction icon="download" label={t('files.download')} onClick={() => onDownload(node)} />
              )}
              {/* Only on a .course, and only when the plan has Review. The
                  extension is a hint, not a check — what the file actually is
                  gets decided by reading the format marker inside it, in
                  web/src/review.ts, once it has been decrypted. */}
              {onSendToReview && isCourseFile(node) && (
                <RowAction
                  icon="message-square"
                  label={t('files.send-for-review')}
                  onClick={() => onSendToReview(node)}
                />
              )}
              <RowAction icon="share-2" label={t('files.share')} onClick={() => onShare(node)} />
              <RowAction icon="user" label={t('files.rename')} onClick={() => onRename(node)} />
              <RowAction icon="trash-2" label={t('files.move-to-trash')} onClick={() => onTrash(node)} />
            </>
          }
        />
        );
      })}
    </DataTable>
  );
}

/**
 * Does this look like a course file?
 *
 * A name check, and deliberately only a hint: it decides whether to *offer* the
 * action, not whether the file is really a course. That question needs the
 * bytes, and the bytes are encrypted until the send actually runs — see
 * `checkCourseJson` in web/src/review.ts, which tests the same `format` marker
 * Compose itself checks on load.
 */
const isCourseFile = (n: DecryptedNode): boolean =>
  n.kind === 'file' && /\.course$/i.test(n.name);

const FILE_COLUMNS: Column[] = [
  { width: 38 },
  { label: 'Name', width: 'flex' },
  // Size goes first and Modified second, each when the name would otherwise be
  // left under about 200px. The fixed columns, gaps and row padding come to
  // 496px with both present.
  { label: 'Size', width: 90, align: 'right', hideBelow: 720 },
  { label: 'Modified', width: 116, align: 'right', hideBelow: 600 },
  // Four 32px buttons plus their gaps.
  { width: 140 }
];

/** The coloured type square. Shared by every listing, so it lives here. */
export function TypeTile({ node, size = 38 }: { node: DecryptedNode; size?: number }) {
  const { Icon } = ds();
  const colours = tint(node.mime);
  return (
    <span
      style={{
        width: size,
        height: size,
        flex: `0 0 ${size}px`,
        borderRadius: 12,
        display: 'grid',
        placeItems: 'center',
        background: colours.bg,
        color: colours.fg
      }}
    >
      <Icon name={fileIcon(node.kind, node.mime)} size={Math.round(size / 2)} />
    </span>
  );
}

function NodeName({ node }: { node: DecryptedNode }) {
  return (
    <>
      <span
        style={{
          display: 'block',
          fontSize: 14.5,
          fontWeight: 600,
          color: node.undecryptable ? 'var(--text-quiet)' : 'var(--text-strong)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
      >
        {node.name}
      </span>
      {node.undecryptable && (
        <span style={{ ...monoPlain, color: 'var(--danger-600)' }}>key mismatch · {node.id}</span>
      )}
    </>
  );
}

export function RowAction({
  icon,
  label,
  onClick
}: {
  icon: string;
  label: string;
  onClick: () => void;
}) {
  const { Icon } = ds();
  const narrow = useIsNarrow();
  // 32px is under every touch-target guideline going. On a phone the row has
  // the width to spare, so the buttons get it.
  const size = narrow ? 40 : 32;
  return (
    <button
      aria-label={label}
      title={label}
      onClick={onClick}
      style={{
        width: size,
        height: size,
        borderRadius: 10,
        border: 'none',
        background: 'transparent',
        color: 'var(--text-muted)',
        cursor: 'pointer',
        transition: `background 120ms ${EASE}`
      }}
    >
      <Icon name={icon} size={narrow ? 17 : 15} />
    </button>
  );
}

function FileGrid({
  nodes,
  onOpen
}: {
  nodes: DecryptedNode[];
  onOpen: (n: DecryptedNode) => void;
}) {
  const sorted = useMemo(() => [...nodes].sort(compareNodes), [nodes]);
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))',
        gap: 16
      }}
    >
      {sorted.map((node) => (
        <GridCard key={node.id} node={node} onOpen={onOpen} />
      ))}
    </div>
  );
}

function GridCard({
  node,
  onOpen
}: {
  node: DecryptedNode;
  onOpen: (n: DecryptedNode) => void;
}) {
  const { Icon } = ds();
  const colours = tint(node.mime);
  const [thumb, setThumb] = useState<string | null>(null);

  useEffect(() => {
    if (!node.hasThumbnail) return;
    let url: string | null = null;
    let cancelled = false;

    const key = getCachedKey(node.id);
    if (key) {
      void thumbnailUrl(node.id, key).then((objectUrl) => {
        url = objectUrl;
        if (cancelled && url) URL.revokeObjectURL(url);
        else if (url) setThumb(url);
      });
    }

    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [node.id, node.hasThumbnail]);

  return (
    <div
      onClick={() => onOpen(node)}
      style={{
        background: 'var(--surface-card)',
        borderRadius: 16,
        boxShadow: '0 1px 0 rgba(40,28,45,.04)',
        overflow: 'hidden',
        // Pointer for both: the click opens a folder and previews a file, and
        // a default cursor on half the cards said one of those was not
        // clickable when it always was.
        cursor: 'pointer'
      }}
    >
      <div
        style={{
          height: 116,
          background: thumb ? 'var(--surface-sunken)' : colours.bg,
          color: colours.fg,
          display: 'grid',
          placeItems: 'center'
        }}
      >
        {thumb ? (
          <img
            src={thumb}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Icon name={fileIcon(node.kind, node.mime)} size={30} />
        )}
      </div>
      <div style={{ padding: '13px 15px 15px' }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: 'var(--text-strong)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {node.name}
        </div>
        <div style={{ ...monoPlain, fontSize: 10.5, marginTop: 4 }}>
          {node.kind === 'folder' ? 'Folder' : formatBytes(node.plainSizeBytes)} ·{' '}
          {formatWhen(node.updatedAt)}
        </div>
      </div>
    </div>
  );
}

function EmptyState({ onChoose, searching }: { onChoose: () => void; searching: boolean }) {
  const t = useT();
  const { Button, Icon } = ds();

  if (searching) {
    return (
      <Panel style={{ padding: '40px 34px' }}>
        <span style={monoPlain}>{t('files.nothing-matched')}</span>
      </Panel>
    );
  }

  return (
    <Panel style={{ padding: '48px 40px', display: 'grid', justifyItems: 'center', gap: 14 }}>
      <span
        style={{
          width: 48,
          height: 48,
          borderRadius: 14,
          background: 'var(--primary-soft)',
          color: 'var(--primary-text)',
          display: 'grid',
          placeItems: 'center'
        }}
      >
        <Icon name="upload-cloud" size={22} />
      </span>
      <h2
        style={{
          fontFamily: "var(--font-display), 'Baloo 2', system-ui",
          fontWeight: 800,
          fontSize: 26,
          margin: 0,
          color: 'var(--text-strong)'
        }}
      >
        {t('files.nothing-here-yet')}
      </h2>
      <span style={{ ...monoPlain, textAlign: 'center' }}>
        {t('files.drop-files-anywhere-on-this')}
      </span>
      <Button variant="secondary" size="md" onClick={onChoose}>
        {t('files.choose-files')}
      </Button>
    </Panel>
  );
}
